import {copyPropertyIdOrNull} from "../formUtils";
import {
    OROZCO_PRODUCT_SPECIAL_ID,
    isPricedProduct,
    isPricedProductAndShouldBePayed,
    isPricedProductButCanBeFree
} from "./orozcoProductUtils";
import _ from 'lodash';
import {getVisitAvailableFreeFreeMeals} from "./visitUtils";


export const updateProductsPaymentsSGroups = [
    'orozco_visit_order_read_orozco_visit_order_products',
    'orozco_visit_order_product_read_id',
    'orozco_visit_order_read_orozco_visit_order_payments',
    'orozco_payment_read_id',
    'orozco_visit_order_product_read',
    'orozco_visit_order_product_read_orozco_product',
    'orozco_visit_order_product_read_orozco_product_modifiers',
    'orozco_product_modifier_read',
    'orozco_visit_order_product_read_data',
    'orozco_product_read'
];

export const emptyOrozcoVisitOrder = (visit) => ({
    orozcoVisitOrderProducts: [],
    scheduledServices: [],
    visit
});

const productsHaveSameModifiers = (x, y) =>
    _.isEqual(x.orozcoProductModifiers, y.orozcoProductModifiers);

const sameOrozcoProduct = (x, y) =>
    x.orozcoProduct === y.orozcoProduct;

const samePayableStatus = (x, y) =>
    x.shouldBePayed === y.shouldBePayed;

const sameNotes = (x, y) =>
    x.notes === y.notes;

const accumulateSameProducts = products => {
    if (products.length === 0) {
        return [];
    }
    const firstProduct = products[0];
    const tail = products.slice(1);
    if (typeof firstProduct === 'string') { // id of previous order, we'll just add it
        return [firstProduct, ...accumulateSameProducts(tail)];
    }
    // Also if this product is a special product, we won't consider
    // duplicates (this is taken care on selection flow)
    if (firstProduct.orozcoProduct === OROZCO_PRODUCT_SPECIAL_ID) {
        return [firstProduct, ...accumulateSameProducts(tail)];
    }

    // if not, this is a new product, we'll check for duplicates.
    // A duplicate is a order product with the same product,
    // same modifiers and same payable status
    const duplicateIndex = _.findIndex(tail, t => typeof t === 'object' ?
        productsHaveSameModifiers(t, firstProduct) &&
        sameOrozcoProduct(t, firstProduct) &&
        samePayableStatus(t, firstProduct) &&
        sameNotes(t, firstProduct)
                                       : false
    );
    if (duplicateIndex === -1) {
        // No duplicates
        return [firstProduct, ...accumulateSameProducts(tail)];
    }

    const tailWithoutDuplicate = [
        ...tail.slice(0, duplicateIndex),
        ...tail.slice(duplicateIndex + 1)
    ];
    // If a duplicate is found, we'll combine them (adding their qtys)
    return accumulateSameProducts([
        {
            ...firstProduct,
            quantity: firstProduct.quantity + tail[duplicateIndex].quantity
        },
        ...tailWithoutDuplicate
    ]);
};

export const prepareOrozcoVisitOrderForServer = (orozcoVisitOrder) => {
    const prepared = {...orozcoVisitOrder, sGroups: addingVisitOrderSGroups};

    copyPropertyIdOrNull(orozcoVisitOrder, prepared, 'visit');
    //Change orderProduct.orozcoProduct objects for the ids
    prepared.orozcoVisitOrderProducts = accumulateSameProducts(
        prepared.orozcoVisitOrderProducts.map(orderProduct => (
            typeof orderProduct === 'string' ?
                orderProduct : {...orderProduct, orozcoProduct: orderProduct.orozcoProduct.id})
        )
    );
    return prepared;
};


export const getPricedMealsThatCanBeFreeCountFromVisitOrder = (orozcoVisitOrder) => {

    return getPricedMealsThatCanBeFreeFromVisitOrder(orozcoVisitOrder)
        .reduce((acc, orderProduct) => acc + orderProduct.quantity, 0);
};

export const getPricedMealsThatCanBeFreeFromVisitOrder = (orozcoVisitOrder) => {
    if (!orozcoVisitOrder || !orozcoVisitOrder.orozcoVisitOrderProducts || !orozcoVisitOrder.orozcoVisitOrderProducts.length)
        return [];
    return _.filter(orozcoVisitOrder.orozcoVisitOrderProducts, (orderProduct) =>
        !orderProduct.canceledDate &&
        !orderProduct.payed &&
        !orderProduct.orozcoProduct.isAlcoholic &&
        isPricedProductButCanBeFree(orderProduct.orozcoProduct) &&
        !( orderProduct.shouldBePayed )
    );
};

export const getPricedAlcoholicDrinksThatCanBeFreeFromVisitOrder = (orozcoVisitOrder) => {
    if (!orozcoVisitOrder || !orozcoVisitOrder.orozcoVisitOrderProducts || !orozcoVisitOrder.orozcoVisitOrderProducts.length)
        return [];

    return _.filter(orozcoVisitOrder.orozcoVisitOrderProducts, (orderProduct) =>
        !orderProduct.canceledDate &&
        !orderProduct.payed &&
        !!orderProduct.orozcoProduct.isAlcoholic &&
        isPricedProductButCanBeFree(orderProduct.orozcoProduct) &&
        !(orderProduct.shouldBePayed)
    );
};

export const getPricedAlcoholicDrinksThatCanBeFreeCountFromVisitOrder = (orozcoVisitOrder) =>
    getPricedAlcoholicDrinksThatCanBeFreeFromVisitOrder(orozcoVisitOrder)
        .reduce((acc, orderProduct) => acc + orderProduct.quantity, 0);

const getVisitAvailableFreeAlcoholicDrinks = (visit) => {
    if (!visit || !visit.checkIns || !visit.checkIns.length)
        return 0;

    return visit.checkIns.reduce((total, checkIn) => {
        if (
            !checkIn ||
            checkIn.canceledDate ||
            !checkIn.activeAdults ||
            !checkIn.loungeAccessMethod ||
            !checkIn.loungeAccessMethod.config ||
            !Number(checkIn.loungeAccessMethod.config.freeAlcoholicDrinks)
        )
            return total;

        return total +
            (
                Number(checkIn.activeAdults) *
                Number(checkIn.loungeAccessMethod.config.freeAlcoholicDrinks)
            );
    }, 0);
};

export const getPricedMealsThatShouldBePayedFromVisitOrder = (orozcoVisitOrder) => {

    if (!orozcoVisitOrder || !orozcoVisitOrder.orozcoVisitOrderProducts || !orozcoVisitOrder.orozcoVisitOrderProducts.length)
        return [];
    return _.filter(orozcoVisitOrder.orozcoVisitOrderProducts, (orderProduct) => !orderProduct.canceledDate && isPricedProductAndShouldBePayed(orderProduct.orozcoProduct));
};

/**
 * Checks all the orderProducts that should be payed and sets the shouldBePayed flag to true on them
 * and to false in all the others.
 *
 * @param selectedOrderProducts
 * @param orozcoVisit
 * @param orozcoVisitOrder if sent, the used product counts will be started from here, instead of from zero
 * @returns {*}
 */
export const setPayableFlagOnSelectedOrderProducts = (selectedOrderProducts, orozcoVisit, orozcoVisitOrder) => {
    const availableMeals = getVisitAvailableFreeFreeMeals(orozcoVisit);
    const availableAlcoholicDrinks =
        getVisitAvailableFreeAlcoholicDrinks(orozcoVisit);

    const usedMeals = orozcoVisitOrder ?
        getPricedMealsThatCanBeFreeCountFromVisitOrder(orozcoVisitOrder) :
        0;

    const usedAlcoholicDrinks = orozcoVisitOrder ?
        getPricedAlcoholicDrinksThatCanBeFreeCountFromVisitOrder(orozcoVisitOrder) :
        0;

    let freeMeals = Math.max(availableMeals - usedMeals, 0);
    let freeAlcoholicDrinks = Math.max(
        availableAlcoholicDrinks - usedAlcoholicDrinks,
        0
    );

    const flaggedOrderProducts = [];

    _.forEach(selectedOrderProducts, (orderProduct) => {
        const newOrderProduct = {
            ...orderProduct,
            payed: !!orderProduct.payed
        };

        if (newOrderProduct.canceledDate || newOrderProduct.payed) {
            flaggedOrderProducts.push(newOrderProduct);
            return;
        }

        if (!isPricedProduct(newOrderProduct.orozcoProduct)) {
            newOrderProduct.shouldBePayed = false;
            flaggedOrderProducts.push(newOrderProduct);
            return;
        }

        if (isPricedProductAndShouldBePayed(newOrderProduct.orozcoProduct)) {
            newOrderProduct.shouldBePayed = true;
            flaggedOrderProducts.push(newOrderProduct);
            return;
        }

        const isAlcoholic =
            !!newOrderProduct.orozcoProduct.isAlcoholic;

        let availableCourtesy = isAlcoholic ?
            freeAlcoholicDrinks :
            freeMeals;

        if (availableCourtesy <= 0) {
            newOrderProduct.shouldBePayed = true;
            flaggedOrderProducts.push(newOrderProduct);
            return;
        }

        if (availableCourtesy >= newOrderProduct.quantity) {
            newOrderProduct.shouldBePayed = false;

            if (isAlcoholic)
                freeAlcoholicDrinks -= newOrderProduct.quantity;
            else
                freeMeals -= newOrderProduct.quantity;

            flaggedOrderProducts.push(newOrderProduct);
            return;
        }

        // Solo una parte de la cantidad tiene cortesía.
        flaggedOrderProducts.push({
            ...orderProduct,
            quantity: newOrderProduct.quantity - availableCourtesy,
            shouldBePayed: true,
            payed: false,
            id: undefined
        });

        newOrderProduct.quantity = availableCourtesy;
        newOrderProduct.shouldBePayed = false;

        if (isAlcoholic)
            freeAlcoholicDrinks = 0;
        else
            freeMeals = 0;

        flaggedOrderProducts.push(newOrderProduct);
    });

    return flaggedOrderProducts;
};

/**
 * Re-calculates the payable flag. Useful when cancelling some products and all payable flags should be re-calculated
 *
 * @param orozcoVisitOrderProducts
 * @param orozcoVisit
 */
export const setPayableFlagOnOrderProducts = (orozcoVisitOrderProducts, orozcoVisit) => {

};

export const removeFreeProductsFromOrder = (orozcoVisitOrderProducts, countToRemove) => {
    const orderProducts = [...orozcoVisitOrderProducts].reverse();
    let newOrderProducts = [];
    for (let i = 0; i < orderProducts.length; i++) {
        if (countToRemove - orderProducts[i].quantity === 0) {
            newOrderProducts = orderProducts.slice(i + 1);
            break;
        } else if (countToRemove - orderProducts[i].quantity < 0) {
            newOrderProducts = orderProducts.slice(i + 1);
            const changedOrderProduct = {...orderProducts[i], quantity: orderProducts[i].quantity - countToRemove};
            newOrderProducts.unshift(changedOrderProduct);
            break;
        } else
            countToRemove -= orderProducts[i].quantity;
    }

    return newOrderProducts.reverse();
};


export const addingVisitOrderSGroups = [
    'orozco_visit_order_read',
    'orozco_visit_order_read_orozco_visit_order_products',
    'orozco_visit_order_product_read',
    'orozco_visit_order_product_read_orozco_product',
    'orozco_visit_order_product_read_orozco_product_modifiers',
    'orozco_product_modifier_read',
    'orozco_visit_order_product_read_data',
    'orozco_product_read',
    "orozco_visit_order_read_notes",
];

export const cancelingVisitOrderProductsSGroups = [
    'orozco_visit_order_read',
    'orozco_visit_order_read_orozco_visit_order_products',
        'orozco_visit_order_product_read',
    'orozco_visit_order_read_orozco_visit_order_payments',
        'orozco_payment_read'
];
